/**
 * Moderator Dashboard Component
 *
 * Main interface for moderators to manage users and content
 */

import { useState } from 'react';
import { BanUserForm } from './BanUserForm';
import { MuteUserForm } from './MuteUserForm';
import { UserStatusChecker } from './UserStatusChecker';
import { AuditLogViewer } from './AuditLogViewer';

type Tab = 'ban' | 'mute' | 'status' | 'audit';

export function ModeratorDashboard() {
  const [activeTab, setActiveTab] = useState<Tab>('status');

  const tabs = [
    { id: 'status' as Tab, label: 'Check Status', icon: '🔍' },
    { id: 'ban' as Tab, label: 'Ban User', icon: '🚫' },
    { id: 'mute' as Tab, label: 'Mute User', icon: '🔇' },
    { id: 'audit' as Tab, label: 'Audit Logs', icon: '📋' },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6">
        <div className="flex items-center gap-3">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div>
            <h2 className="text-2xl font-bold text-white">Moderator Dashboard</h2>
            <p className="text-blue-100 text-sm mt-1">Manage users and enforce community guidelines</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex -mb-px">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex-1 flex items-center justify-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors
                ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }
              `}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'status' && <UserStatusChecker />}
        {activeTab === 'ban' && <BanUserForm />}
        {activeTab === 'mute' && <MuteUserForm />}
        {activeTab === 'audit' && <AuditLogViewer />}
      </div>
    </div>
  );
}
